interface PermutiveIdentity {
    id: string;
    tag: string;
    priority?: number;
}

/**
 * Fetches the PXID from permutive, which is cross-domain identity of the user, and returns
 * the Permutive identity object tagged as `pxid`.
 * We read the permutive config from the window object, and we use `credentials: 'include'`
 * to send the cookie with the request, so that Permutive can identify the user.
 */

export const fetchPermutiveIdentity = async (): Promise<PermutiveIdentity | undefined> => {
    const { apiKey, workspaceId, pxidHost } = window.permutive?.config ?? {};

    // If the _pxid.id property isn't truthy, issue the request and set it
    if (!window._pxid?.id && apiKey && workspaceId && pxidHost) {
        const url = `https://${workspaceId}.${pxidHost}/v2.0/pxid?k=${apiKey}`;
        try {
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await response.json();
            if (data && data.uid) {
                window._pxid = {
                    id: data.uid,
                    tag: 'pxid',
                    priority: 1,
                };
            }
        } catch {}
    }

    return window._pxid ?? undefined;
};

/**
 * Checks if the user is logged in as a subscriber, and if so, returns the
 * Permutive identity tagged as `maven_id`.
 */
export const getLoggedInIdentity = (): PermutiveIdentity | undefined => {
    const userStatus = window.phxGetLoggedInStatus?.();
    if (userStatus) {
        if (userStatus.type === 'rgis' && userStatus.id) {
            return {
                id: userStatus.id,
                tag: 'maven_id',
                priority: 7,
            };
        }
    }
};

/**
 * Reads the `panoramaId` from local storage, and returns the Permutive identity
 * object tagged as `panorama_id`, if the value exists.
 */
export const getPanoramaIdentity = (): PermutiveIdentity | undefined => {
    const panoramaId = localStorage?.getItem?.('panoramaId');
    if (typeof panoramaId === 'string' && panoramaId) {
        return {
            id: panoramaId,
            tag: 'panorama_id',
            priority: 4,
        };
    }
};

export const getPpidIdentity = (): PermutiveIdentity | undefined => {
    const permutiveId = localStorage?.getItem?.('permutive-id');
    if (typeof permutiveId === 'string' && permutiveId) {
        return {
            id: `arenaGroup-${permutiveId}`,
            tag: 'ppid',
            priority: 5,
        };
    }
};

/**
 * Reads the Amazon shared ID from the the local storage, and returns the Permutive
 * identity object tagged as `shared_id`, if the value exists.
 */
export const getAmazonSharedIdentity = (): PermutiveIdentity | undefined => {
    const sharedId = localStorage?.getItem?.('_pubcid');
    if (typeof sharedId === 'string' && sharedId) {
        return {
            id: sharedId,
            tag: 'shared_id',
            priority: 8,
        };
    }
};

/**
 * Reads the ID5 shared ID from the the local storage, and returns the Permutive
 * identity object tagged as `id5`, if the value exists.
 */
export const getId5Identity = (): PermutiveIdentity | undefined => {
    const id5Identity = localStorage?.getItem?.('id5id');
    if (!id5Identity) {
        return;
    }

    try {
        const value = JSON.parse(decodeURIComponent(id5Identity));
        const universalId = value?.universal_uid;
        if (universalId && universalId !== '0') {
            return {
                id: universalId,
                tag: 'id5',
                priority: 6,
            };
        }
    } catch {}
};

/**
 * Reads the `userSync.userIds` config from Prebid, and attempts to find the
 * `connect_id` and `liveramp_ats` values. If they exist, they are returned
 * as Permutive identity objects.
 */
export const getPbjsIdentities = async (): Promise<PermutiveIdentity[]> => {
    const idMap = {
        connectId: {
            tag: 'connect_id',
            priority: 9,
        },
        idl_env: {
            tag: 'liveramp_ats',
            priority: 3,
        },
    };

    return new Promise((resolve) => {
        const pbjs = window.pbjs;
        if (typeof pbjs?.que?.push === 'function') {
            pbjs.que.push(() => {
                // Get a fresh reference to window.pbjs which may have been
                // reassigned before processing the queue.
                const _pbjs = window.pbjs;
                // This is mostly for satisfying the TS compiler
                if (!_pbjs) {
                    resolve([]);
                    return;
                }

                // If the async function of getUserIds lasts more than 500 ms we just return
                // the userIds that were loaded until now.
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                const timeoutPromise = new Promise<PrebidUserIds>((resolve) => {
                    setTimeout(() => {
                        resolve(_pbjs?.getUserIds());
                    }, 500);
                });

                const identities: PermutiveIdentity[] = [];
                Promise.race([timeoutPromise, _pbjs.getUserIdsAsync()])
                    .then((userIds) => {
                        if (userIds) {
                            Object.entries(userIds).forEach(([key, value_1]) => {
                                const idConfig = idMap[key as keyof typeof idMap];
                                if (idConfig) {
                                    identities.push({ ...idConfig, id: value_1 });
                                }
                            });
                        }
                        resolve(identities);
                    })
                    .catch(() => resolve([]));
            });
        } else {
            resolve([]);
        }
    });
};

export const getIterableEndUserIdentity = async (): Promise<PermutiveIdentity[] | undefined> => {
    try {
        const emailHashData = window?.localStorage?.getItem?.('emailHashes');
        if (!emailHashData) return;

        const data: { alg: string; digest: string }[] = JSON.parse(emailHashData);
        if (!Array.isArray(data) || data.length === 0) return;

        const sha256Hash = data.find((hash) => hash?.alg === 'sha256');
        if (sha256Hash) {
            return [
                {
                    id: sha256Hash.digest,
                    tag: 'hashed_email',
                    priority: 0,
                },
                {
                    id: sha256Hash.digest,
                    tag: 'email_sha256',
                },
            ];
        }
    } catch (error) {}
    return;
};
/**
 * Collects all the identities from various sources and combines them into a single
 * array that can be passed to `permutive.identify` function
 */
export const collectIdentities = async (): Promise<PermutiveIdentity[]> => {
    const identities = await Promise.all([
        fetchPermutiveIdentity(),
        getPpidIdentity(),
        getId5Identity(),
        getLoggedInIdentity(),
        getPanoramaIdentity(),
        getAmazonSharedIdentity(),
        getPbjsIdentities(),
        getIterableEndUserIdentity(),
    ]);

    return identities
        .flatMap((identity) => {
            if (identity !== undefined) {
                return Array.isArray(identity) ? identity : [identity];
            }
            return [];
        })
        .filter((identity) => !window?.phxBooleans[`disable-${identity.tag}-identity`]);
};
